import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  Box,
  Typography,
  Button,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  FormControl,
  Checkbox,
  Autocomplete,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DescriptionIcon from '@mui/icons-material/Description';
import TableViewIcon from '@mui/icons-material/TableView';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { styled } from '@mui/material/styles';
import FileUploadNote from '../../Common/FileUploadNote';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../../Constants/EditorStyles.css';  // Import custom editor styles
import { RICH_TEXT_EDITOR_CONFIG, insertFieldButton, insertFieldValue } from '../../../Constants/EditorConfig';
import './StandardInputField.css';
import Menu from '@mui/material/Menu';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";

// Register the custom button
insertFieldButton(Quill);

// Add custom toolbar button
const CustomToolbar = Quill.import('ui/icons');
CustomToolbar['insert-field'] = '<svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M19 3H5c-1.11 0-2 .89-2 2v14c0 1.11.89 2 2 2h14c1.11 0 2-.89 2-2V5c0-1.11-.89-2-2-2m-2 10H7v-2h10v2Z"/></svg>';

const UploadButton = styled(Button)(({ theme }) => ({
  border: '2px dashed #BDBDBD',
  color: '#9E9E9E',
  borderColor: '#BDBDBD',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '240px',
  width: '240px',
  padding: theme.spacing(2),
  borderRadius: '12px',
  backgroundColor: '#ffffff',
  '&:hover': {
    backgroundColor: '#f8f9fa',
    borderColor: '#757575',
  },
}));

const getFileIcon = (fileName) => {
  if (!fileName) return <InsertDriveFileIcon sx={{ fontSize: 48 }} />;
  
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'xlsx':
    case 'xls':
    case 'csv':
      return <TableViewIcon sx={{ fontSize: 48, color: '#217346' }} />;
    case 'doc':
    case 'docx':
      return <DescriptionIcon sx={{ fontSize: 48, color: '#2B579A' }} />;
    case 'pdf':
      return <PictureAsPdfIcon sx={{ fontSize: 48, color: '#B30B00' }} />;
    default:
      return <InsertDriveFileIcon sx={{ fontSize: 48 }} />;
  }
};

const isImageFile = (fileName) => {
  if (!fileName) return false;
  const extension = fileName.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
};

const FilePreview = ({ fileName, fileUrl, file }) => {
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    // Clean up previous preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    // Create preview URL for file being uploaded
    if (file && isImageFile(file.name)) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    // Clear preview URL if no file
    else if (!fileUrl) {
      setPreviewUrl(null);
    }
  }, [file, fileUrl]);

  if (isImageFile(fileName) && (fileUrl || previewUrl)) {
    return (
      <Box 
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <img 
          src={fileUrl || previewUrl}
          alt={fileName}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '12px'
          }}
        />
      </Box>
    );
  }

  return (
    <Box className="file-preview">
      {getFileIcon(fileName)}
    </Box>
  );
};

const FileControls = ({ viewMode, onDownload, onDelete }) => {
  if (viewMode) return null;
  
  return (
    <Box className="file-controls">
      <IconButton onClick={onDownload} className="file-control-button">
        <DownloadIcon />
      </IconButton>
      <IconButton onClick={onDelete} className="file-control-button">
        <DeleteIcon />
      </IconButton>
    </Box>
  );
};

const FileDetails = ({ fileName, fileSize, uploadedBy = 'John Doe', uploadedDate = new Date().toISOString(), fileUrl }) => {
  const getFileType = (name) => {
    if (!name) return 'Unknown';
    const extension = name.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'xlsx':
      case 'xls':
        return 'Excel Spreadsheet';
      case 'doc':
      case 'docx':
        return 'Word Document';
      case 'pdf':
        return 'PDF Document';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'Image';
      default:
        return 'Document';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).replace(',', '');
  };

  return (
    <Box sx={{ 
      p: 2,
      bgcolor: '#1a1a1a',
      width: '280px',
      borderRadius: '4px'
    }}>
      {isImageFile(fileName) && fileUrl && (
        <Box sx={{ 
          width: '100%',
          height: '140px',
          mb: 2,
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <img 
            src={fileUrl}
            alt={fileName}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        </Box>
      )}
      <Typography 
        sx={{ 
          fontSize: '15px',
          fontWeight: 600,
          color: '#ffffff',
          mb: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          lineHeight: 1.3,
          maxHeight: '60px'
        }}
      >
        {fileName || 'Unknown'}
      </Typography>
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: '#9ca3af', width: '80px' }}>
            Type:
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#ffffff' }}>
            {getFileType(fileName)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: '#9ca3af', width: '80px' }}>
            Size:
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#ffffff' }}>
            {fileSize ? `${(fileSize / 1048576).toFixed(2)} MB` : 'Unknown'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: '#9ca3af', width: '80px' }}>
            Uploaded by:
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#ffffff' }}>
            {uploadedBy}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: '#9ca3af', width: '80px' }}>
            Uploaded on:
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#ffffff' }}>
            {formatDate(uploadedDate)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const PreviewUploadButton = ({ file, onUpload, disabled, validationConfig }) => {
  return (
    <UploadButton
      variant="outlined"
      component="label"
      className="policy-template-modal-image-container"
      disabled={disabled}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        ...(file && {
          border: '2px dashed #9E9E9E',
          '&:hover': {
            borderColor: '#757575',
            '& .upload-overlay': {
              opacity: 1
            }
          }
        })
      }}
    >
      {file ? (
        <>
          <FilePreview
            fileName={file.name}
            file={file}
          />
          <Box
            className="upload-overlay"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.2s ease'
            }}
          >
            <Typography sx={{ color: '#666', mb: 1 }}>Click to change file</Typography>
          </Box>
        </>
      ) : (
        <Box sx={{ 
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <Box sx={{
            width: '100px',
            height: '100px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            mb: 2
          }}>
            <InsertDriveFileIcon sx={{ fontSize: 48, color: '#9E9E9E' }} />
          </Box>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#9E9E9E',
              textAlign: 'center',
              lineHeight: 1.2
            }}
          >
            Select file to upload
          </Typography>
        </Box>
      )}
      <input
        type="file"
        hidden
        onChange={onUpload}
        disabled={disabled}
        accept={validationConfig?.accept}
      />
    </UploadButton>
  );
};

/**
 * StandardInputField Component
 * 
 * A versatile input field component that supports various types of inputs including text, multiline, select, and file uploads.
 * 
 * @param {Object} props
 * @param {string} props.type - Input type ('text', 'email', 'password', 'number', 'multiline', 'select', 'file')
 * @param {string} props.name - Field name
 * @param {string} props.label - Field label
 * @param {any} props.value - Field value
 * @param {function} props.onChange - Change handler
 * @param {boolean} props.required - Whether field is required
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {Object} props.config - Additional configuration
 * @param {boolean} props.viewMode - Whether field is in view mode
 * @param {Object} props.fileData - File data for file type
 * @param {Object} props.fileHandlers - File handlers for file type
 * @param {Object} props.validationConfig - Validation configuration
 * @param {Array} props.options - Options for select type
 * 
 * @example
 * // Basic text input
 * <StandardInputField
 *   type="text"
 *   name="username"
 *   label="Username"
 *   value={username}
 *   onChange={handleChange}
 *   required={true}
 * />
 * 
 * @example
 * // Multiline text input (Method 1 - using multiline type)
 * <StandardInputField
 *   type="multiline"
 *   name="description"
 *   label="Description"
 *   value={description}
 *   onChange={handleChange}
 *   config={{
 *     minRows: 3,
 *     maxRows: 15,
 *     fullWidth: true
 *   }}
 * />
 * 
 * @example
 * // Multiline text input (Method 2 - using text type with multiline config)
 * <StandardInputField
 *   type="text"
 *   name="description"
 *   label="Description"
 *   value={description}
 *   onChange={handleChange}
 *   config={{
 *     multiline: true,
 *     minRows: 3,
 *     fullWidth: true
 *   }}
 * />
 * 
 * @example
 * // Select input
 * <StandardInputField
 *   type="select"
 *   name="category"
 *   label="Category"
 *   value={category}
 *   onChange={handleChange}
 *   options={[
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' }
 *   ]}
 * />
 * 
 * @example
 * // File input
 * <StandardInputField
 *   type="file"
 *   name="document"
 *   label="Upload Document"
 *   fileData={fileData}
 *   fileHandlers={{
 *     onUpload: handleUpload,
 *     onDownload: handleDownload,
 *     onDelete: handleDelete
 *   }}
 *   validationConfig={{
 *     accept: '.pdf,.doc,.docx'
 *   }}
 * />
 */
const StandardInputField = ({
  type = 'text',
  name,
  label,
  value,
  onChange,
  required = false,
  error,
  helperText,
  disabled = false,
  config = {},
  viewMode,
  fileData,
  fileHandlers,
  validationConfig,
  options = []
}) => {
  // Move hooks to the top level
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [quillRef, setQuillRef] = React.useState(null);
  const [currentRange, setCurrentRange] = React.useState(null);

  // Date related hooks moved to top level
  const parseDate = useCallback((dateStr) => {
    if (!dateStr) return null;
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  }, []);

  const formatDate = useCallback((date) => {
    if (!date || isNaN(date.getTime())) return null;
    try {
      return format(date, config.formatPattern || 'dd/MM/yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return null;
    }
  }, [config.formatPattern]);

  const handleDateChange = useCallback((newValue) => {
    onChange({
      target: {
        name,
        value: formatDate(newValue)
      }
    });
  }, [onChange, name, formatDate]);

  const parsedValue = useMemo(() => type === 'date' ? parseDate(value) : null, [type, value, parseDate]);

  React.useEffect(() => {
    if (type === 'richtext') {
      const handleInsertField = (event) => {
        setCurrentRange(event.detail.range);
        // Position the menu near the toolbar button
        const button = document.querySelector('.ql-insert-field');
        if (button) {
          setAnchorEl(button);
        }
      };

      document.addEventListener('insertField', handleInsertField);
      return () => document.removeEventListener('insertField', handleInsertField);
    }
  }, [type]);

  const handleInsertField = (fieldName, fieldValue) => {
    if (quillRef && currentRange) {
      insertFieldValue(quillRef.getEditor(), currentRange, fieldName, fieldValue);
    }
    setAnchorEl(null);
  };

  const textFieldProps = {
    variant: config.variant || 'filled',
    fullWidth: config.fullWidth !== false,
    name: name,
    label: (
      <>
        {label}
        {required && <span style={{ color: 'red' }}> *</span>}
      </>
    ),
    value: value || '',
    onChange: onChange,
    margin: 'dense',
    error: !!error,
    helperText: helperText,
    disabled: disabled,
    InputLabelProps: {
      className: 'InputLabelProp-style',
      focused: { className: 'focus-style' },
    },
    InputProps: {
      className: config.multiline ? 'InputProps-style-address' : 'InputProps-style',
    }
  };

  if (config.multiline) {
    textFieldProps.multiline = true;
    textFieldProps.minRows = config.minRows || 3;
    textFieldProps.maxRows = 15;
    textFieldProps.sx = {
      '.MuiInputBase-inputMultiline': {
        display: 'flex',
        alignItems: 'flex-start',
        height: '100%',
        paddingLeft: '5px',
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word',
      },
      '.MuiInputBase-root': {
        height: 'auto',
      }
    };
  }

  switch (type) {
    case 'text':
    case 'email':
    case 'password':
    case 'number':
      if (config.uiStyle === 'field_styling_25') {
        return (
          <Box sx={{ mb: 2, width: '100%' }}>
            <Typography
              variant="body2"
              sx={{
                color: '#374151',
                fontWeight: 500,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              {label}
              {required && <span style={{ color: 'red' }}> *</span>}
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              name={name}
              value={value || ''}
              onChange={onChange}
              disabled={disabled}
              type={type}
              multiline={!!config.multiline}
              minRows={config.minRows || (config.multiline ? 3 : undefined)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
                  borderRadius: '8px',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { 
                    borderColor: disabled ? '#E5E7EB' : '#D1D5DB' 
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: disabled ? '#E5E7EB' : '#3B82F6' 
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' }
                  }
                },
                '& .MuiInputBase-input': {
                  color: disabled ? '#6B7280' : '#111827',
                  cursor: disabled ? 'not-allowed' : 'text'
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: '#6B7280',
                  cursor: 'not-allowed'
                }
              }}
              error={!!error}
              helperText={helperText}
              placeholder={config.placeholder || ''}
            />
          </Box>
        );
      }
      return <TextField {...textFieldProps} type={type} />;

    case 'phone':
      if (config.uiStyle === 'field_styling_25') {
        return (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              sx={{ color: '#374151', fontWeight: 500, mb: 1 }}
            >
              {label}
              {required && <span style={{ color: 'red' }}> *</span>}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <FormControl sx={{ minWidth: 130 }} disabled={disabled}>
                <Select
                  displayEmpty
                  value={config.countryCode?.value || ""}
                  onChange={config.countryCode?.onChange}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 300, overflowY: 'scroll', scrollbarWidth: 'thin' }
                    }
                  }}
                  renderValue={(selectedValue) => {
                    if (!selectedValue || selectedValue === "|") return <span className="SelectLabel-style">Code</span>;
                    const [code, callingCode] = selectedValue.split("|");
                    return callingCode ? `${code} (${callingCode})` : `${code}`;
                  }}
                  sx={{
                    backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D1D5DB' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3B82F6' }
                  }}
                >
                  {config.countryCode?.options?.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                variant="outlined"
                value={config.phoneNumber?.value || ""}
                onChange={config.phoneNumber?.onChange}
                type="tel"
                InputProps={{
                  readOnly: disabled,
                  disabled: disabled,
                  inputProps: { inputMode: 'numeric', pattern: '[0-9]*' }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
                    borderRadius: '8px',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                    '&.Mui-focused fieldset': { borderColor: '#3B82F6' }
                  },
                  '& .MuiInputBase-input': { color: disabled ? '#6B7280' : '#111827' }
                }}
                error={!!config.phoneNumber?.error}
                helperText={config.phoneNumber?.error}
              />
            </Box>
          </Box>
        );
      }
      return (
        <Box className="phoneNumberContainer">
          <Box className="phoneNumberSubContainer">
            <Select
              displayEmpty
              fullWidth
              id="country-code-label"
              value={config.countryCode?.value || ""}
              onChange={config.countryCode?.onChange}
              className="MuiSelect-select"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    overflowY: 'scroll',
                    scrollbarWidth: 'thin',
                  },
                  sx: {
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#888',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      backgroundColor: '#555',
                    },
                  },
                },
              }}
              inputProps={{ readOnly: disabled, disabled: disabled }}
              renderValue={(selectedValue) => {
                if (!selectedValue || selectedValue === "|") {
                  return <span className="SelectLabel-style">Code</span>;
                }
                const [code, callingCode] = selectedValue.split("|");
                return callingCode ? `${code} (${callingCode})` : `${code}`;
              }}
            >
              {config.countryCode?.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box className="phoneInput">
            <TextField
              label="Phone Number"
              variant="filled"
              fullWidth
              value={config.phoneNumber?.value || ""}
              onChange={config.phoneNumber?.onChange}
              margin="dense"
              type="tel"
              InputLabelProps={{
                className: "InputLabelProp-style",
                focused: { className: "focus-style" },
              }}
              InputProps={{
                readOnly: disabled,
                disabled: disabled,
                inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                className: "InputProps-style",
              }}
              error={!!config.phoneNumber?.error}
              helperText={config.phoneNumber?.error}
            />
          </Box>
        </Box>
      );

    case 'date':
      if (config.uiStyle === 'field_styling_25') {
        return (
          <Box sx={{ mb: 2, width: '100%' }}>
            <Typography
              variant="body2"
              sx={{
                color: '#374151',
                fontWeight: 500,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              {label}
              {required && <span style={{ color: 'red' }}> *</span>}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={enUS}>
              <DatePicker
                value={parsedValue}
                onChange={handleDateChange}
                disabled={disabled}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!error,
                    helperText: helperText,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
                        borderRadius: '8px',
                        '& fieldset': { borderColor: '#E5E7EB' },
                        '&:hover fieldset': {
                          borderColor: disabled ? '#E5E7EB' : '#D1D5DB'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: disabled ? '#E5E7EB' : '#3B82F6'
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#F9FAFB',
                          '& fieldset': { borderColor: '#E5E7EB' }
                        }
                      },
                      '& .MuiInputBase-input': {
                        color: disabled ? '#6B7280' : '#111827',
                        cursor: disabled ? 'not-allowed' : 'text'
                      },
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: '#6B7280',
                        cursor: 'not-allowed'
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        );
      }
      return (
        <Box sx={{ marginBottom: '16px' }}>
          <p className="uploadTitle" style={{ marginBottom: '4px' }}>
            {label}
            {required && <span style={{ color: 'red' }}> *</span>}
          </p>
          <LocalizationProvider dateAdapter={AdapterDateFns} locale={enUS}>
            <DatePicker
              value={parsedValue}
              onChange={handleDateChange}
              disabled={disabled}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!error,
                  helperText: helperText,
                  sx: {
                    '& .MuiInputBase-root': {
                      backgroundColor: '#ffffff',
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>
        </Box>
      );

    case 'radio':
      return (
        <Box mt={2} style={{ width: "100%" }}>
          <FormLabel component="legend">
            {label}
            {required && <span style={{ color: 'red' }}> *</span>}
          </FormLabel>
          <RadioGroup
            value={value.toString()}
            onChange={onChange}
            row={config.row}
          >
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
                disabled={disabled}
              />
            ))}
          </RadioGroup>
          {error && (
            <Typography color="error" variant="caption">
              {helperText}
            </Typography>
          )}
        </Box>
      );

    case 'multiline':
      if (config.uiStyle === 'field_styling_25') {
        return (
          <Box sx={{ mb: 2, width: '100%' }}>
            <Typography
              variant="body2"
              sx={{
                color: '#374151',
                fontWeight: 500,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              {label}
              {required && <span style={{ color: 'red' }}> *</span>}
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              name={name}
              value={value || ''}
              onChange={onChange}
              disabled={disabled}
              multiline
              minRows={config.minRows || 3}
              maxRows={config.maxRows || 15}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
                  borderRadius: '8px',
                  alignItems: 'flex-start',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': {
                    borderColor: disabled ? '#E5E7EB' : '#D1D5DB'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: disabled ? '#E5E7EB' : '#3B82F6'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' }
                  }
                },
                '& .MuiInputBase-input': {
                  color: disabled ? '#6B7280' : '#111827',
                  cursor: disabled ? 'not-allowed' : 'text',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: '#6B7280',
                  cursor: 'not-allowed'
                }
              }}
              error={!!error}
              helperText={helperText}
              placeholder={config.placeholder || ''}
            />
          </Box>
        );
      }
      return (
        <TextField
          {...textFieldProps}
          multiline
          minRows={config.minRows || 3}
          maxRows={config.maxRows || 15}
          sx={{
            '.MuiInputBase-inputMultiline': {
              display: 'flex',
              alignItems: 'flex-start',
              height: '100%',
              paddingLeft: '5px',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              marginTop: '5%',
            },
            '.MuiInputBase-root': {
              height: 'auto',
            }
          }}
        />
      );

    case 'richtext':
      return (
        <Box sx={{ width: '100%', marginBottom: 2 }}>
          <Typography 
            variant="body1" 
            sx={{ 
              marginBottom: 1,
              fontWeight: 600,
              fontSize: '14px',
              color: '#7e8299'
            }}
          >
            {label}
            {required && <span style={{ color: 'red' }}> *</span>}
          </Typography>
          <Box 
            sx={{ 
              border: '1px solid #E4E6EF',
              borderRadius: '4px',
              '.ql-container': {
                minHeight: '150px',
                fontSize: '14px',
                border: '1px solid #E4E6EF',
                borderTop: 'none',
                borderBottomLeftRadius: '4px',
                borderBottomRightRadius: '4px'
              },
              '.ql-editor': {
                minHeight: '150px',
                backgroundColor: '#ffffff'
              },
              '.ql-toolbar': {
                display: disabled ? 'none' : 'block',
                border: '1px solid #E4E6EF',
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
                backgroundColor: '#ffffff',
                borderBottom: disabled ? 'none' : '1px solid #E4E6EF'
              },
              '.ql-editor.ql-blank::before': {
                color: '#A1A5B7'
              }
            }}
          >
            <ReactQuill
              ref={(el) => {
                if (el) {
                  setQuillRef(el);
                }
              }}
              value={value || ''}
              onChange={(content) => {
                onChange({ 
                  target: { 
                    name, 
                    value: content 
                  } 
                });
              }}
              modules={config.editorConfig?.modules || RICH_TEXT_EDITOR_CONFIG.modules}
              readOnly={disabled}
              theme="snow"
            />
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem onClick={() => handleInsertField('risk_type', config.fieldValues?.risk_type || '')}>
              Insert Risk Type
            </MenuItem>
            <MenuItem onClick={() => handleInsertField('risk_ref', config.fieldValues?.risk_ref || '')}>
              Insert Risk Reference
            </MenuItem>
          </Menu>
          {error && (
            <Typography color="error" variant="caption">
              {helperText}
            </Typography>
          )}
        </Box>
      );

    case 'autocomplete':
    case 'select': {
      const useAutocomplete = type === 'autocomplete' || !!config.searchable;
      if (useAutocomplete) {
        const isMultiple = !!config.multiple;
        const selectedValues = isMultiple
          ? (Array.isArray(value) ? value : (value ? [value] : []))
          : value;
        const selectedOptions = isMultiple
          ? options.filter((opt) => selectedValues.some((v) => String(opt.value) === String(v) || opt.value === v))
          : options.find((opt) => String(opt.value) === String(selectedValues) || opt.value === selectedValues) || null;

        const autocompleteField = (
          <Autocomplete
            multiple={isMultiple}
            options={options}
            value={isMultiple ? selectedOptions : selectedOptions}
            disabled={disabled || !!config.disabled}
            getOptionLabel={(option) => option?.label ?? ''}
            isOptionEqualToValue={(option, val) =>
              String(option?.value) === String(val?.value) || option?.value === val?.value
            }
            onChange={(_, newValue) => {
              if (isMultiple) {
                const next = Array.isArray(newValue) ? newValue.map((opt) => opt.value) : [];
                onChange({ target: { name, value: next } });
              } else {
                onChange({ target: { name, value: newValue ? newValue.value : '' } });
              }
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={String(option.value)}
                  label={option.label}
                  size="small"
                />
              ))
            }
            renderOption={(props, option, { index }) => (
              <li {...props} key={String(option.value)}>
                {config.renderMenuItem ? config.renderMenuItem(option, index, options) : option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                name={name}
                placeholder={config.placeholder || `Select ${label.toLowerCase()}`}
                error={!!error}
                helperText={error ? helperText : undefined}
                sx={
                  config.uiStyle === 'field_styling_25'
                    ? {
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#E5E7EB' },
                          '&:hover fieldset': { borderColor: '#D1D5DB' },
                          '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
                        },
                      }
                    : undefined
                }
              />
            )}
          />
        );

        if (config.uiStyle === 'field_styling_25') {
          return (
            <Box sx={{ mb: 2, width: '100%' }}>
              <Typography
                variant="body2"
                sx={{ color: '#374151', fontWeight: 500, mb: 1 }}
              >
                {label}
                {required && <span style={{ color: 'red' }}> *</span>}
              </Typography>
              {autocompleteField}
              {config.afterElement}
            </Box>
          );
        }

        return (
          <Box sx={{ marginBottom: '16px' }}>
            <p className="uploadTitle" style={{ marginBottom: '4px' }}>
              {label}
              {required && <span style={{ color: 'red' }}> *</span>}
            </p>
            {autocompleteField}
            {config.afterElement}
          </Box>
        );
      }

      if (config.uiStyle === 'field_styling_25') {
        return (
          <Box sx={{ mb: 2, width: '100%' }}>
            <Typography
              variant="body2"
              sx={{ color: '#374151', fontWeight: 500, mb: 1 }}
            >
              {label}
              {required && <span style={{ color: 'red' }}> *</span>}
            </Typography>
            <FormControl fullWidth error={!!error} disabled={disabled}>
              <Select
                name={name}
                multiple={!!config.multiple}
                value={config.multiple ? (Array.isArray(value) ? value : (value ? [value] : [])) : (value !== undefined ? value : '')}
                onChange={onChange}
                displayEmpty
                MenuProps={config.MenuProps || {
                  PaperProps: {
                    className: 'select-menu',
                    style: { maxHeight: 200, overflowY: 'auto' }
                  }
                }}
                renderValue={config.renderValue || ((selected) => {
                  if (config.multiple) {
                    const arr = Array.isArray(selected) ? selected : [];
                    if (arr.length === 0) return `Select ${label.toLowerCase()}`;
                    const labels = arr.map(val => {
                      const opt = options.find(o => String(o.value) === String(val) || o.value === val);
                      return opt ? opt.label : val;
                    }).filter(Boolean);
                    return labels.length > 1 ? `${labels[0]}, +${labels.length - 1} more` : labels[0];
                  }
                  if (selected === undefined || selected === '') return `Select ${label.toLowerCase()}`;
                  const option = options.find(opt => String(opt.value) === String(selected) || opt.value === selected);
                  return option ? option.label : selected;
                })}
                sx={{
                  backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D1D5DB' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3B82F6' }
                }}
              >
                {!config.multiple && (
                  <MenuItem value="" disabled>Select {label.toLowerCase()}</MenuItem>
                )}
                {options.map((option, idx) => (
                  <MenuItem 
                    key={String(option.value)} 
                    value={option.value}
                    sx={config.menuItemSx ? config.menuItemSx(option, idx, options) : undefined}
                  >
                    {config.multiple && config.withCheckbox && (
                      <Checkbox checked={(Array.isArray(value) ? value : (value ? [value] : [])).indexOf(option.value) > -1} />
                    )}
                    {config.renderMenuItem ? config.renderMenuItem(option, idx, options) : option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {error && (
              <Typography color="error" variant="caption">
                {helperText}
              </Typography>
            )}
            {config.afterElement}
          </Box>
        );
      }
      return (
        <Box sx={{ marginBottom: '16px' }}>
          <p className="uploadTitle" style={{ marginBottom: '4px' }}>
            {label}
            {required && <span style={{ color: 'red' }}> *</span>}
          </p>
          <Select
            name={name}
            multiple={!!config.multiple}
            value={config.multiple ? (Array.isArray(value) ? value : (value ? [value] : [])) : (value !== undefined ? value : '')}
            onChange={onChange}
            fullWidth={config.fullWidth !== false}
            displayEmpty
            error={!!error}
            disabled={disabled}
            MenuProps={config.MenuProps || { 
              PaperProps: { 
                className: 'select-menu',
                style: {
                  maxHeight: 200,
                  overflowY: 'auto'
                }
              } 
            }}
            renderValue={config.renderValue || ((selected) => {
              if (config.multiple) {
                const arr = Array.isArray(selected) ? selected : [];
                if (arr.length === 0) return `Select ${label.toLowerCase()}`;
                const labels = arr.map(val => {
                  const opt = options.find(o => String(o.value) === String(val) || o.value === val);
                  return opt ? opt.label : val;
                }).filter(Boolean);
                return labels.length > 1 ? `${labels[0]}, +${labels.length - 1} more` : labels[0];
              }
              if (selected === undefined || selected === '') return `Select ${label.toLowerCase()}`;
              const option = options.find(opt => String(opt.value) === String(selected) || opt.value === selected);
              return option ? option.label : selected;
            })}
          >
            {!config.multiple && (
              <MenuItem value="" disabled>
                Select {label.toLowerCase()}
              </MenuItem>
            )}
            {options.map((option, idx) => (
              <MenuItem 
                key={String(option.value)} 
                value={option.value}
                sx={config.menuItemSx ? config.menuItemSx(option, idx, options) : undefined}
              >
                {config.multiple && config.withCheckbox && (
                  <Checkbox checked={(Array.isArray(value) ? value : (value ? [value] : [])).indexOf(option.value) > -1} />
                )}
                {config.renderMenuItem ? config.renderMenuItem(option, idx, options) : option.label}
              </MenuItem>
            ))}
          </Select>
          {error && (
            <Typography color="error" variant="caption">
              {helperText}
            </Typography>
          )}
          {config.afterElement}
        </Box>
      );
    }

    case 'file':
      return (
        <Box className="uploadContainer">
          <Box className="uploadSubContainer" sx={{ width: '100%' }}>
            <p className="uploadTitle">
              {label}
              {required && <span style={{ color: 'red' }}> *</span>}
            </p>
            {!viewMode && validationConfig && (
              <Box sx={{ width: '100%', mb: 2 }}>
                <FileUploadNote {...validationConfig} />
              </Box>
            )}
            
            {/* Upload Button */}
            <Box sx={{ width: '100%', mb: 2 }}>
              {((type === 'multifile' && (!fileData?.files || fileData?.files?.length < (config?.maxFiles || 5))) || 
                (type === 'file' && !fileData?.file)) && (
                <>
                  <input
                    type="file"
                    onChange={fileHandlers?.onUpload}
                    style={{ display: 'none' }}
                    id={`${name}-upload`}
                    accept={validationConfig?.accept}
                    multiple={type === 'multifile'}
                    disabled={viewMode}
                  />
                  <label htmlFor={`${name}-upload`}>
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={viewMode}
                      sx={{
                        border: '2px dashed #BDBDBD',
                        color: '#9E9E9E',
                        width: '100%',
                        height: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                          backgroundColor: '#E8E8E8',
                          borderColor: '#757575',
                        }
                      }}
                    >
                      <UploadFileIcon sx={{ fontSize: 40 }} />
                      <Typography>{type === 'multifile' ? 'Click to upload files' : 'Click to upload file'}</Typography>
                      {type === 'multifile' && (
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          (Maximum {config.maxFiles || 5} files)
                        </Typography>
                      )}
                    </Button>
                  </label>
                </>
              )}
            </Box>

            {/* File Display */}
            {(fileData?.file || fileData?.fileUrl) && (
              <Box sx={{ 
                mt: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                width: '100%'
              }}>
                {config.displayStyle === 'list' ? (
                  // List View (New Design)
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: '#f5f5f5'
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      flex: 1,
                      minWidth: 0
                    }}>
                      <InsertDriveFileIcon sx={{ color: '#1D3DFC' }} />
                      <Typography
                        sx={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {fileData?.fileName || fileData?.file?.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {(fileData?.fileUrl || fileData?.url) && (
                        <IconButton
                          size="small"
                          onClick={fileHandlers?.onDownload}
                          sx={{ color: '#1D3DFC' }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      )}
                      {!viewMode && (
                        <IconButton
                          size="small"
                          onClick={fileHandlers?.onDelete}
                          sx={{ color: '#d32f2f' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ) : (
                  // Card View (Old Design)
                  <Box
                    sx={{
                      width: '240px',
                      height: '240px',
                      position: 'relative',
                      border: '2px solid #e0e0e0',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      backgroundColor: '#ffffff',
                      '&:hover': {
                        '& .file-controls': {
                          opacity: 1
                        }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                      }}
                    >
                      <Box
                        sx={{
                          width: '100px',
                          height: '100px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '16px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                          mb: 2
                        }}
                      >
                        {getFileIcon(fileData?.fileName || fileData?.file?.name)}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#1a1a1a',
                          textAlign: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: 1.2,
                          mb: 1
                        }}
                      >
                        {fileData?.fileName || fileData?.file?.name}
                      </Typography>
                    </Box>
                    {/* Hover Controls */}
                    <Box
                      className="file-controls"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        opacity: 0,
                        transition: 'opacity 0.2s ease'
                      }}
                    >
                      {(fileData?.fileUrl || fileData?.url) && (
                        <IconButton
                          onClick={fileHandlers?.onDownload}
                          sx={{
                            backgroundColor: '#1D3DFC',
                            color: '#ffffff',
                            '&:hover': {
                              backgroundColor: '#1631c8'
                            }
                          }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      )}
                      {!viewMode && (
                        <IconButton
                          onClick={fileHandlers?.onDelete}
                          sx={{
                            backgroundColor: '#d32f2f',
                            color: '#ffffff',
                            '&:hover': {
                              backgroundColor: '#a82424'
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      );

    case 'multifile':
      return (
        <Box className="uploadContainer">
          <Box className="uploadSubContainer" sx={{ width: '100%' }}>
            <p className="uploadTitle">
              {label}
              {required && <span style={{ color: 'red' }}> *</span>}
            </p>
            {!viewMode && validationConfig && (
              <Box sx={{ width: '100%', mb: 2 }}>
                <FileUploadNote {...validationConfig} />
              </Box>
            )}
            
            {/* Upload Button */}
            <Box sx={{ width: '100%', mb: 2 }}>
              {((type === 'multifile' && (!fileData?.files || fileData?.files?.length < (config?.maxFiles || 5))) || 
                (type === 'file' && !fileData?.file)) && (
                <>
                  <input
                    type="file"
                    onChange={fileHandlers?.onUpload}
                    style={{ display: 'none' }}
                    id={`${name}-upload`}
                    accept={validationConfig?.accept}
                    multiple={type === 'multifile'}
                    disabled={viewMode}
                  />
                  <label htmlFor={`${name}-upload`}>
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={viewMode}
                      sx={{
                        border: '2px dashed #BDBDBD',
                        color: '#9E9E9E',
                        width: '100%',
                        height: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                          backgroundColor: '#E8E8E8',
                          borderColor: '#757575',
                        }
                      }}
                    >
                      <UploadFileIcon sx={{ fontSize: 40 }} />
                      <Typography>{type === 'multifile' ? 'Click to upload files' : 'Click to upload file'}</Typography>
                      {type === 'multifile' && (
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          (Maximum {config.maxFiles || 5} files)
                        </Typography>
                      )}
                    </Button>
                  </label>
                </>
              )}
            </Box>

            {/* File List */}
            {fileData?.files?.length > 0 && (
              <Box sx={{ 
                mt: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                width: '100%'
              }}>
                {fileData.files.map((file, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: '#f5f5f5'
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      flex: 1,
                      minWidth: 0
                    }}>
                      <InsertDriveFileIcon sx={{ color: '#1D3DFC' }} />
                      <Typography
                        sx={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {file.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {file.url && (
                        <IconButton
                          size="small"
                          onClick={() => fileHandlers?.onDownload?.(file)}
                          sx={{ color: '#1D3DFC' }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      )}
                      {!viewMode && (
                        <IconButton
                          size="small"
                          onClick={() => fileHandlers?.onDelete?.(file)}
                          sx={{ color: '#d32f2f' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      );

    default:
      return <TextField {...textFieldProps} />;
  }
};

export default StandardInputField; 